import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Flame, AlertTriangle, Compass, ChevronRight, ArrowUpRight,
  Briefcase, Building2, Megaphone, Users, Cpu, Newspaper, Activity,
  Crosshair, X, Clock, Target, Mail, MessageSquare, Phone, Calendar, CheckCircle2,
  Send, Loader2, RotateCw, Linkedin, Search, CheckSquare, Square,
} from "lucide-react";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

const TIER_COLOR = {
  cold: "text-muted-foreground border-border bg-muted/30",
  warm: "text-amber-accent border-amber-accent/40 bg-amber-accent/10",
  hot: "text-signal-500 border-signal-500/40 bg-signal-500/10",
  blazing: "text-destructive border-destructive/40 bg-destructive/10",
};
const RISK_COLOR = {
  low: "text-muted-foreground border-border bg-muted/30",
  medium: "text-amber-accent border-amber-accent/40 bg-amber-accent/10",
  high: "text-orange-400 border-orange-400/40 bg-orange-400/10",
  critical: "text-destructive border-destructive/40 bg-destructive/10",
};
const SIG_ICON = {
  hiring_spike: Briefcase, warehouse_expansion: Building2, funding_round: Megaphone,
  leadership_change: Users, tech_adoption: Cpu, news_mention: Newspaper,
  engagement_uptick: Activity, competitor_signal: Crosshair,
};
const ACTION_ICON = {
  send_linkedin_message: MessageSquare, send_email: Mail, call: Phone,
  schedule_meeting: Calendar, share_content: Sparkles, wait: Clock, disqualify: X,
};

const SEND_ERROR_MSG = {
  no_verified_email: "No verified email for this prospect — find their email first.",
  no_linkedin_profile: "Couldn't resolve this person on LinkedIn — open their profile to add a valid LinkedIn URL, then retry.",
  no_connected_linkedin_account: "Connect your LinkedIn account first (Integrations).",
  invite_pending: "Connection request already sent — wait for them to accept before messaging.",
  linkedin_daily_cap_reached: "LinkedIn daily message cap reached — try again tomorrow.",
  linkedin_invite_cap_reached: "LinkedIn daily invite cap reached — try again tomorrow.",
  outreach_blocked: "This contact is on the suppression list or an existing ERP customer — outreach is blocked.",
  manual_action: "This is a phone-call action — handle it manually.",
  nothing_to_send: "There's no message draft to send.",
  already_handled: "This recommendation was already handled.",
  prospect_not_found: "Prospect not found.",
};

const TABS = [
  { key: "feed", label: "Intent Feed" },
  { key: "hot", label: "Hot Prospects" },
  { key: "risk", label: "Deal Risk" },
  { key: "nba", label: "Next-Best Actions" },
];

export default function SalesCopilotPage() {
  const [tab, setTab] = useState("feed");
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState([]);
  const [hot, setHot] = useState([]);
  const [risk, setRisk] = useState(null);
  const [nbas, setNbas] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [genFor, setGenFor] = useState(null);
  const [quickSend, setQuickSend] = useState(null); // { prospect_id, prospect_name }
  const [m365Connected, setM365Connected] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, f, h, r, n] = await Promise.all([
        api.get("/intent/summary"),
        api.get("/intent/feed?limit=50"),
        api.get("/intent/prospects/hot?limit=20"),
        api.get("/intent/deal-risk?limit=50"),
        api.get("/intent/recommendations?limit=30"),
      ]);
      setSummary(s.data);
      setFeed(f.data.events || []);
      setHot(h.data.prospects || []);
      setRisk(r.data);
      setNbas((n.data.recommendations || []).filter((r) => r.status !== "superseded"));
    } catch {
      toast.error("Failed to load Sales Copilot data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    api.get("/m365/status")
      .then(({ data }) => setM365Connected(!!data?.connected))
      .catch(() => setM365Connected(false));
  }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/intent/scan?force=true&limit=50");
      toast.success(`Scanned ${data.scanned} prospects, ${data.skipped_recent} skipped (fresh).`);
      await loadAll();
    } catch {
      toast.error("Scan failed");
    }
    setScanning(false);
  };

  const generateNBA = async (prospectId, prospectName) => {
    setGenFor(prospectId);
    try {
      const { data } = await api.post(`/intent/recommend/${prospectId}`);
      setNbas((prev) => [data, ...prev]);
      toast.success(`Next-best-action ready for ${prospectName}`);
      setTab("nba");
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("AI budget reached — adjust caps in AI Budget.");
      } else {
        toast.error("Failed to generate recommendation");
      }
    }
    setGenFor(null);
  };

  const dismissEvent = async (id) => {
    try {
      await api.post(`/intent/dismiss/${id}`);
      setFeed((prev) => prev.filter((e) => e.id !== id));
      toast.success("Signal dismissed");
    } catch { toast.error("Could not dismiss"); }
  };

  const setNbaStatus = async (id, status) => {
    try {
      await api.patch(`/intent/recommendations/${id}?status=${status}`);
      setNbas((prev) => prev.map((n) => (n.id === id ? { ...n, status } : n)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const sendNba = async (n) => {
    try {
      const { data } = await api.post(`/intent/recommendations/${n.id}/send`);
      const modeLabel = data.mode === "invite_sent" ? "Connection request sent"
        : data.mode === "dm_sent" ? "LinkedIn message sent" : "Email sent";
      toast.success(`${modeLabel} to ${n.prospect_name}`);
      setNbas((prev) => prev.map((x) => (x.id === n.id ? { ...x, status: "done", sent_mode: data.mode } : x)));
      return true;
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(SEND_ERROR_MSG[err] || err || "Could not send");
      return false;
    }
  };

  const [regenerating, setRegenerating] = useState(false);
  const regenerateAll = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/intent/recommendations/regenerate?limit=50");
      toast.success(`Refreshed ${data.regenerated} recommendation(s)`, {
        description: "Updated with the latest Europacific messaging.",
      });
      await loadAll();
    } catch {
      toast.error("Could not regenerate recommendations");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <DashboardLayout
      eyebrow="AI Sales Copilot"
      title="Buying intent & next-best actions"
      actions={
        <Button onClick={runScan} disabled={scanning} data-testid="copilot-scan-btn" className="rounded-sm">
          <Sparkles className="w-4 h-4 mr-1.5" />{scanning ? "Scanning..." : "Run intent scan"}
        </Button>
      }
    >
      {/* Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 grid-borders mb-7" data-testid="copilot-kpis">
        <KPI
          icon={Flame} label="Blazing prospects"
          value={(summary?.intent?.tier_counts?.blazing || 0) + (summary?.intent?.tier_counts?.hot || 0)}
          sub={`${summary?.intent?.total_signals || 0} total signals tracked`}
          accent="text-signal-500"
        />
        <KPI
          icon={AlertTriangle} label="At-risk deals"
          value={(summary?.risk?.tier_counts?.critical || 0) + (summary?.risk?.tier_counts?.high || 0)}
          sub={`${summary?.risk?.count || 0} active in funnel`}
          accent="text-destructive"
        />
        <KPI
          icon={Compass} label="Open recommendations"
          value={summary?.nba_open || 0}
          sub="ready to execute"
          accent="text-amber-accent"
        />
        <KPI
          icon={Target} label="Prospects scored"
          value={summary?.intent?.total_scored || 0}
          sub="across this workspace"
          accent="text-foreground"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-border" data-testid="copilot-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`copilot-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-signal-500 text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "feed" && (
        <IntentFeed feed={feed} onDismiss={dismissEvent} onGenerateNBA={generateNBA} genFor={genFor} />
      )}
      {tab === "hot" && (
        <HotProspects hot={hot} onGenerateNBA={generateNBA} genFor={genFor} onQuickSend={setQuickSend} m365Connected={m365Connected} onReload={loadAll} />
      )}
      {tab === "risk" && (
        <DealRiskView risk={risk} onGenerateNBA={generateNBA} genFor={genFor} />
      )}
      {tab === "nba" && (
        <NBAList nbas={nbas} onStatus={setNbaStatus} onSend={sendNba} onRegenerateAll={regenerateAll} regenerating={regenerating} />
      )}

      {quickSend && (
        <QuickSendModal
          prospectId={quickSend.prospect_id}
          prospectName={quickSend.prospect_name}
          onClose={() => setQuickSend(null)}
        />
      )}
    </DashboardLayout>
  );
}

function KPI({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="p-6 flex flex-col gap-1.5 bg-card/40">
      <div className={`flex items-center gap-2 ${accent || ""}`}>
        <Icon className="w-4 h-4" />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-display text-3xl font-semibold tracking-tightest">{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function TierPill({ tier, kind = "intent" }) {
  const cls = (kind === "risk" ? RISK_COLOR : TIER_COLOR)[tier] || TIER_COLOR.cold;
  return <span className={`px-2 py-0.5 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border ${cls}`}>{tier}</span>;
}

function IntentFeed({ feed, onDismiss, onGenerateNBA, genFor }) {
  if (feed.length === 0) {
    return <EmptyState msg="No intent signals yet. Run an intent scan to generate them across your prospects." />;
  }
  return (
    <div className="space-y-3">
      {feed.map((e) => {
        const Icon = SIG_ICON[e.signal_type] || Sparkles;
        return (
          <div key={e.id} className="data-card" data-testid={`feed-event-${e.id}`}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-1 flex md:block items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-signal-500/10 border border-signal-500/30 flex items-center justify-center text-signal-500">
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div className="col-span-12 md:col-span-8 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-display font-semibold text-sm">{e.label}</span>
                  <span className="signal-pill">{e.category}</span>
                  <span className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" /> {e.age_days === 0 ? "today" : `${e.age_days}d ago`}
                  </span>
                  {e.prospect_tier && <TierPill tier={e.prospect_tier} />}
                </div>
                <div className="text-sm">{e.evidence}</div>
                <Link to={`/app/prospects/${e.prospect_id}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1">
                  {e.prospect_name} · {e.company} · {e.title} <ArrowUpRight className="w-3 h-3" />
                </Link>
                <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground mt-1">
                  source: {e.source.replace(/_/g, " ")}
                </div>
              </div>
              <div className="col-span-12 md:col-span-3 flex md:flex-col gap-2 md:items-end">
                <div className="md:text-right">
                  <div className="font-mono text-xl font-semibold">{e.prospect_score?.toFixed(1) || "—"}</div>
                  <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">intent score</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onGenerateNBA(e.prospect_id, e.prospect_name)}
                  disabled={genFor === e.prospect_id}
                  data-testid={`feed-nba-${e.id}`}
                >
                  {genFor === e.prospect_id ? "..." : "Recommend action"} <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDismiss(e.id)}
                  data-testid={`feed-dismiss-${e.id}`}
                  className="text-muted-foreground"
                >
                  <X className="w-3 h-3 mr-1" /> Dismiss
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HotProspects({ hot, onGenerateNBA, genFor, onQuickSend, m365Connected, onReload }) {
  const [items, setItems] = useState(hot);
  const [enriching, setEnriching] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkSendConfirm, setBulkSendConfirm] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkSendStatus, setBulkSendStatus] = useState("");
  useEffect(() => { setItems(hot); }, [hot]);

  const noEmail = items.filter((p) => !p.has_email);
  const withEmail = items.filter((p) => p.has_email);
  const selectedSet = new Set(selected);
  const selectedWithEmail = withEmail.filter((p) => selectedSet.has(p.prospect_id));

  const toggleSelect = (id) => {
    setBulkSendConfirm(false);
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllEmail = () => {
    setBulkSendConfirm(false);
    const ids = withEmail.map((p) => p.prospect_id);
    setSelected(selectedWithEmail.length === withEmail.length ? [] : ids);
  };
  const clearSelection = () => { setSelected([]); setBulkSendConfirm(false); };

  const runBulkSend = async () => {
    const ids = selectedWithEmail.map((p) => p.prospect_id);
    if (!ids.length) return;
    setBulkSending(true);
    setBulkSendStatus(`Sending 0/${ids.length}…`);
    try {
      const { data } = await api.post("/m365/quick-send-bulk", { prospect_ids: ids });
      if (!data.job_id) { setBulkSending(false); toast.error("Could not start bulk send"); return; }
      toast.info(`AI-drafting + sending to ${data.total} prospects…`);
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/m365/quick-send-bulk/${data.job_id}`);
          const r = job.result || {};
          setBulkSendStatus(`Sending ${r.processed || 0}/${job.total || ids.length}…`);
          if (job.status === "done") {
            clearInterval(poll); setBulkSending(false); setBulkSendStatus("");
            setBulkSendConfirm(false); setSelected([]);
            toast.success("Bulk send complete", {
              description: `${r.sent || 0} sent · ${r.skipped_no_email || 0} skipped (no email) · ${r.failed || 0} failed`,
            });
            onReload && onReload();
          } else if (job.status === "failed") {
            clearInterval(poll); setBulkSending(false); setBulkSendStatus("");
            toast.error("Bulk send failed", { description: job.error || "" });
          }
        } catch { /* keep polling */ }
      }, 3000);
      setTimeout(() => { clearInterval(poll); setBulkSending(false); }, 600000);
    } catch (e) {
      setBulkSending(false); setBulkSendStatus("");
      toast.error(e?.response?.data?.detail || "Bulk send failed");
    }
  };

  const runBulkEnrich = async () => {
    setBulkEnriching(true);
    try {
      const ids = noEmail.map((p) => p.prospect_id);
      const { data } = await api.post("/apollo/enrich-prospects-bulk", { prospect_ids: ids });
      if (!data.job_id) {
        toast.info("Nothing to enrich", { description: data.message || "These leads already have emails." });
        setBulkEnriching(false); setBulkConfirm(false); return;
      }
      toast.info(`Finding emails for ${data.total} leads…`);
      const poll = setInterval(async () => {
        try {
          const { data: job } = await api.get(`/apollo/enrich-icp/${data.job_id}`);
          if (job.status === "done") {
            clearInterval(poll); setBulkEnriching(false); setBulkConfirm(false);
            const r = job.result || {};
            toast.success("Enrichment complete", {
              description: `${r.verified_emails || 0} emails found · ${r.matched_no_email || 0} matched (no email) · ${r.no_match || 0} no match`,
            });
            onReload && onReload();
          } else if (job.status === "failed") {
            clearInterval(poll); setBulkEnriching(false); setBulkConfirm(false);
            toast.error("Enrichment failed", { description: job.error || "" });
          }
        } catch { /* keep polling */ }
      }, 3000);
      setTimeout(() => { clearInterval(poll); setBulkEnriching(false); }, 300000);
    } catch (e) {
      setBulkEnriching(false);
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    }
  };

  const LI_LABEL = {
    replied: { t: "Replied on LinkedIn", c: "text-emerald-400 border-emerald-400/40" },
    dm_sent: { t: "DM sent", c: "text-signal-500 border-signal-500/40" },
    connected: { t: "Connected", c: "text-sky-400 border-sky-400/40" },
    invited: { t: "Invite sent", c: "text-amber-accent border-amber-accent/40" },
    not_contacted: { t: "Not contacted", c: "text-muted-foreground border-border" },
  };

  const findEmail = async (p) => {
    setEnriching(p.prospect_id);
    try {
      const { data } = await api.post(`/apollo/enrich-prospect/${p.prospect_id}`);
      if (data.found_email) {
        toast.success(`Found ${data.email}`, { description: "Lead is now ready for the email flow." });
        setItems((prev) => prev.map((x) => x.prospect_id === p.prospect_id
          ? { ...x, has_email: true, email: data.email, email_verified: data.email_verified } : x));
      } else {
        toast.warning("No verified email found in Apollo", { description: "Reach them on LinkedIn instead." });
        setItems((prev) => prev.map((x) => x.prospect_id === p.prospect_id ? { ...x, apollo_enriched: true } : x));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    }
    setEnriching(null);
  };

  if (items.length === 0) return <EmptyState msg="Run an intent scan to surface hot prospects." />;
  return (
    <div data-testid="hot-prospects-wrap">
      {/* Multi-select bulk Quick Send toolbar */}
      {withEmail.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap" data-testid="hot-select-toolbar">
          <button onClick={selectAllEmail} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="hot-select-all">
            {selectedWithEmail.length === withEmail.length && withEmail.length > 0
              ? <CheckSquare className="w-3.5 h-3.5 text-signal-500" /> : <Square className="w-3.5 h-3.5" />}
            {selectedWithEmail.length === withEmail.length && withEmail.length > 0 ? "Deselect all" : `Select all with email (${withEmail.length})`}
          </button>
          {selectedWithEmail.length > 0 && (
            bulkSendConfirm ? (
              <div className="flex items-center gap-2 text-sm" data-testid="hot-bulksend-confirm-bar">
                <span className="text-muted-foreground">AI-draft + send to <strong>{selectedWithEmail.length}</strong> prospects via M365?</span>
                <Button size="sm" onClick={runBulkSend} disabled={bulkSending} data-testid="hot-bulksend-confirm" className="bg-signal-500 hover:bg-signal-500/90 text-white">
                  {bulkSending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {bulkSendStatus || "Sending…"}</> : "Confirm & send"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setBulkSendConfirm(false)} disabled={bulkSending} data-testid="hot-bulksend-cancel">Cancel</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{selectedWithEmail.length} selected</span>
                <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="hot-clear-selection" className="text-xs">Clear</Button>
                <Button size="sm" onClick={() => setBulkSendConfirm(true)} disabled={!m365Connected}
                  title={m365Connected ? "AI-draft + send to all selected" : "Connect Microsoft 365 in Settings first"}
                  data-testid="hot-bulksend-btn"
                  className="bg-signal-500 hover:bg-signal-500/90 text-white disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60">
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Send to {selectedWithEmail.length} selected
                </Button>
              </div>
            )
          )}
        </div>
      )}
      {noEmail.length > 0 && (
        <div className="flex items-center justify-end mb-3">
          {bulkConfirm ? (
            <div className="flex items-center gap-2 text-sm" data-testid="hot-bulk-confirm-bar">
              <span className="text-muted-foreground">Look up emails for <strong>{noEmail.length}</strong> leads · ~{noEmail.length} Apollo credits</span>
              <Button size="sm" onClick={runBulkEnrich} disabled={bulkEnriching} data-testid="hot-bulk-enrich-confirm" className="bg-signal-500 hover:bg-signal-500/90 text-white">
                {bulkEnriching ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Enriching…</> : "Confirm"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setBulkConfirm(false)} disabled={bulkEnriching} data-testid="hot-bulk-enrich-cancel">Cancel</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setBulkConfirm(true)} data-testid="hot-bulk-enrich-btn" className="rounded-sm">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Find emails for all ({noEmail.length})
            </Button>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="hot-prospects-grid">
      {items.map((p) => {
        const li = LI_LABEL[p.li_status] || LI_LABEL.not_contacted;
        const isSel = selectedSet.has(p.prospect_id);
        return (
        <div key={p.prospect_id} className={`data-card ${isSel ? "ring-1 ring-signal-500/60" : ""}`} data-testid={`hot-prospect-${p.prospect_id}`}>
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-start gap-2 min-w-0">
              {p.has_email && (
                <button onClick={() => toggleSelect(p.prospect_id)} className="mt-1 shrink-0" data-testid={`hot-select-${p.prospect_id}`}
                  title={isSel ? "Deselect" : "Select for bulk send"}>
                  {isSel ? <CheckSquare className="w-4 h-4 text-signal-500" /> : <Square className="w-4 h-4 text-muted-foreground hover:text-foreground" />}
                </button>
              )}
              <div className="min-w-0">
              <Link to={`/app/prospects/${p.prospect_id}`} className="font-display font-semibold hover:underline inline-flex items-center gap-1">
                {p.prospect_name} <ArrowUpRight className="w-3 h-3" />
              </Link>
              <div className="text-xs text-muted-foreground">{p.title}</div>
              <div className="text-xs text-muted-foreground">{p.company}</div>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                <span className={`text-[0.62rem] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${li.c}`}
                  data-testid={`hot-li-status-${p.prospect_id}`}>
                  <Linkedin className="w-2.5 h-2.5" /> {li.t}
                </span>
                <span className={`text-[0.62rem] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${p.has_email ? "text-emerald-400 border-emerald-400/40" : "text-muted-foreground border-border"}`}
                  data-testid={`hot-email-status-${p.prospect_id}`}>
                  <Mail className="w-2.5 h-2.5" /> {p.has_email ? "Email ✓" : "No email"}
                </span>
              </div>
            </div>
            </div>
            <TierPill tier={p.tier} />
          </div>
          <div className="flex items-end justify-between mt-3 gap-2">
            <div>
              <div className="font-mono text-3xl font-semibold text-signal-500">{p.score?.toFixed(1)}</div>
              <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">intent · {p.event_count} signals</div>
            </div>
            <div className="flex flex-col gap-1.5 items-end">
              {p.has_email ? (
                <Button
                  size="sm"
                  onClick={() => onQuickSend({ prospect_id: p.prospect_id, prospect_name: p.prospect_name })}
                  disabled={!m365Connected}
                  title={m365Connected ? "AI-draft + send email in 2 clicks" : "Connect Microsoft 365 in Settings first"}
                  data-testid={`hot-quicksend-${p.prospect_id}`}
                  className="bg-signal-500 hover:bg-signal-500/90 text-white disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="w-3 h-3 mr-1" /> Quick Send
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => findEmail(p)}
                  disabled={enriching === p.prospect_id}
                  title="Look up a verified email via Apollo so this lead can enter the email flow"
                  data-testid={`hot-find-email-${p.prospect_id}`}
                  className="bg-signal-500 hover:bg-signal-500/90 text-white"
                >
                  {enriching === p.prospect_id
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Searching…</>
                    : <><Search className="w-3 h-3 mr-1" /> Find email</>}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onGenerateNBA(p.prospect_id, p.prospect_name)}
                disabled={genFor === p.prospect_id}
                data-testid={`hot-nba-${p.prospect_id}`}
              >
                {genFor === p.prospect_id ? "..." : "Recommend"} <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
        );
      })}
      </div>
    </div>
  );
}

function DealRiskView({ risk, onGenerateNBA, genFor }) {
  if (!risk) return null;
  if (risk.count === 0) return <EmptyState msg="No active-funnel prospects yet. Move prospects into Contacted / Engaged to evaluate deal risk." />;
  return (
    <>
      <div className="grid grid-cols-4 gap-0 grid-borders mb-5" data-testid="risk-tier-counts">
        {["critical", "high", "medium", "low"].map((t) => (
          <div key={t} className="p-4">
            <div className="label-eyebrow">{t}</div>
            <div className={`font-display text-2xl font-semibold ${RISK_COLOR[t].split(" ")[0]}`}>{risk.tier_counts[t]}</div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {risk.rows.map((r) => (
          <div key={r.prospect_id} className="data-card" data-testid={`risk-row-${r.prospect_id}`}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-3 min-w-0">
                <Link to={`/app/prospects/${r.prospect_id}`} className="font-display font-semibold hover:underline inline-flex items-center gap-1">
                  {r.prospect_name} <ArrowUpRight className="w-3 h-3" />
                </Link>
                <div className="text-xs text-muted-foreground">{r.title}</div>
                <div className="text-xs text-muted-foreground">{r.company}</div>
                <div className="mt-2 flex gap-1 flex-wrap">
                  <span className="signal-pill">{r.status}</span>
                  {r.days_since_touch !== null && (
                    <span className="signal-pill">{r.days_since_touch}d since touch</span>
                  )}
                </div>
              </div>
              <div className="col-span-12 md:col-span-7">
                <ul className="space-y-1 text-sm">
                  {r.reasons.map((reason, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                        reason.severity === "high" ? "text-destructive" :
                        reason.severity === "medium" ? "text-amber-accent" : "text-muted-foreground"
                      }`} />
                      <span className="text-muted-foreground">{reason.msg}</span>
                    </li>
                  ))}
                  {r.reasons.length === 0 && <li className="text-muted-foreground italic">No risk factors detected.</li>}
                </ul>
              </div>
              <div className="col-span-12 md:col-span-2 flex md:flex-col gap-2 md:items-end">
                <div className="md:text-right">
                  <div className="font-mono text-2xl font-semibold">{r.risk_score}</div>
                  <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">risk</div>
                </div>
                <TierPill tier={r.risk_tier} kind="risk" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onGenerateNBA(r.prospect_id, r.prospect_name)}
                  disabled={genFor === r.prospect_id}
                  data-testid={`risk-nba-${r.prospect_id}`}
                >
                  {genFor === r.prospect_id ? "..." : "Save the deal"}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function NBAList({ nbas, onStatus, onSend, onRegenerateAll, regenerating }) {
  const [confirmId, setConfirmId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const openCount = nbas.filter((n) => n.status === "open" || !n.status).length;
  const RegenButton = openCount > 0 ? (
    <Button
      variant="outline" size="sm" onClick={onRegenerateAll} disabled={regenerating}
      className="rounded-sm" data-testid="nba-regenerate-all-btn"
    >
      {regenerating
        ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Refreshing…</>
        : <><RotateCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate all ({openCount})</>}
    </Button>
  ) : null;

  if (nbas.length === 0) return <EmptyState msg="No recommendations yet. Click 'Recommend' on a hot prospect or at-risk deal to generate one." />;
  return (
    <div className="space-y-3" data-testid="nba-list">
      {RegenButton && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Refresh all open cards with the latest Europacific messaging in one click.</p>
          {RegenButton}
        </div>
      )}
      {nbas.map((n) => {
        const Icon = ACTION_ICON[n.action_type] || Sparkles;
        const isDone = n.status === "done";
        const isDismissed = n.status === "dismissed";
        const channelKind = (n.channel === "email" || n.action_type === "send_email") ? "email"
          : (n.channel === "linkedin" || n.action_type === "send_linkedin_message") ? "linkedin" : null;
        const sendable = !!channelKind && !!n.draft && !isDone && !isDismissed;
        const viaLabel = channelKind === "email" ? "email" : "LinkedIn";
        return (
          <div
            key={n.id}
            className={`data-card ${isDone ? "opacity-60" : ""} ${isDismissed ? "opacity-40" : ""}`}
            data-testid={`nba-${n.id}`}
          >
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-3 min-w-0">
                <Link to={`/app/prospects/${n.prospect_id}`} className="font-display font-semibold hover:underline inline-flex items-center gap-1">
                  {n.prospect_name} <ArrowUpRight className="w-3 h-3" />
                </Link>
                <div className="text-xs text-muted-foreground">{n.title}</div>
                <div className="text-xs text-muted-foreground">{n.company}</div>
                <div className="mt-2 flex gap-1 flex-wrap">
                  <TierPill tier={n.intent_tier} />
                  <TierPill tier={n.risk_tier} kind="risk" />
                </div>
              </div>
              <div className="col-span-12 md:col-span-7">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-signal-500/10 border border-signal-500/30 text-signal-500 text-xs font-medium">
                    <Icon className="w-3 h-3" /> {n.action_type?.replace(/_/g, " ")}
                  </span>
                  <span className="signal-pill">urgency: {n.urgency?.replace(/_/g, " ")}</span>
                  <span className="signal-pill">via {n.channel}</span>
                  <span className="text-[0.65rem] text-muted-foreground">{relativeTime(n.created_at)}</span>
                </div>
                {n.reasoning && (
                  <div className="text-xs text-muted-foreground italic mb-2">"{n.reasoning}"</div>
                )}
                {n.subject && (
                  <div className="text-xs mb-1.5"><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{n.subject}</span></div>
                )}
                {n.draft && (
                  <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/30 border border-border rounded-sm p-3 max-h-44 overflow-y-auto" data-testid={`nba-draft-${n.id}`}>{n.draft}</pre>
                )}
                {Array.isArray(n.talking_points) && n.talking_points.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {n.talking_points.map((tp, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <ChevronRight className="w-3 h-3 mt-0.5 text-signal-500 flex-shrink-0" /> <span>{tp}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="col-span-12 md:col-span-2 flex md:flex-col gap-2 md:items-end">
                {confirmId === n.id ? (
                  <div className="w-full rounded-sm border border-signal-500/40 bg-signal-500/5 p-3 space-y-2" data-testid={`nba-confirm-${n.id}`}>
                    <div className="text-xs font-medium flex items-center gap-1.5">
                      {channelKind === "email" ? <Mail className="w-3.5 h-3.5 text-signal-500" /> : <Linkedin className="w-3.5 h-3.5 text-signal-500" />}
                      Send to {n.prospect_name} via {viaLabel}?
                    </div>
                    {channelKind === "linkedin" && (
                      <p className="text-[0.65rem] text-muted-foreground">If you're not connected yet, a connection request is sent with the first 300 characters as the note.</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={async () => { setSendingId(n.id); const ok = await onSend(n); setSendingId(null); if (ok) setConfirmId(null); }}
                        disabled={sendingId === n.id}
                        data-testid={`nba-send-confirm-${n.id}`}
                      >
                        {sendingId === n.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        Send now
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)} disabled={sendingId === n.id} data-testid={`nba-send-cancel-${n.id}`}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {sendable && (
                      <Button
                        size="sm"
                        onClick={() => setConfirmId(n.id)}
                        className="bg-signal-500 hover:bg-signal-500/90 text-white"
                        data-testid={`nba-send-${n.id}`}
                      >
                        <Send className="w-3 h-3 mr-1" /> Approve & send
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onStatus(n.id, "done")}
                      disabled={isDone || isDismissed}
                      data-testid={`nba-done-${n.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Mark done
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onStatus(n.id, "dismissed")}
                      disabled={isDone || isDismissed}
                      data-testid={`nba-dismiss-${n.id}`}
                      className="text-muted-foreground"
                    >
                      Dismiss
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="data-card text-center py-12 text-sm text-muted-foreground" data-testid="copilot-empty">
      {msg}
    </div>
  );
}

function QuickSendModal({ prospectId, prospectName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({ to: [], cc: [], subject: "", body: "" });

  const fetchDraft = useCallback(async (force = false) => {
    setRegen(true);
    setError(null);
    try {
      const url = `/m365/quick-draft/${prospectId}${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      setDraft({
        to: data.to || [],
        cc: data.cc || [],
        subject: data.subject || "",
        body: data.body || "",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate draft.");
    }
    setLoading(false);
    setRegen(false);
  }, [prospectId]);

  useEffect(() => { fetchDraft(false); }, [fetchDraft]);

  const send = async () => {
    if (!draft.to.length || !draft.subject.trim() || !draft.body.trim()) {
      toast.error("Subject, body and recipient are required");
      return;
    }
    setSending(true);
    try {
      const esc = draft.body
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const html = "<p>" + esc.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>") + "</p>";
      await api.post("/m365/send", {
        to: draft.to,
        cc: draft.cc.length ? draft.cc : null,
        subject: draft.subject,
        html_body: html,
        prospect_id: prospectId,
      });
      toast.success(`Sent to ${prospectName}`);
      onClose();
    } catch (e) {
      const detail = (e?.response?.data?.detail || "Send failed").toString().slice(0, 200);
      toast.error(detail);
    }
    setSending(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quicksend-modal"
    >
      <div
        className="bg-card border border-border rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-signal-500" />
            <div>
              <div className="label-eyebrow">Quick Send · AI draft</div>
              <div className="font-display font-semibold">{prospectName}</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="quicksend-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> AI is drafting your email…
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="text-sm text-destructive mb-3">{error}</div>
            <Button onClick={() => fetchDraft(false)} variant="outline" size="sm" data-testid="quicksend-retry">
              <RotateCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <Field label="To">
              <input
                type="text"
                value={draft.to.join(", ")}
                onChange={(e) => setDraft({ ...draft, to: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                className="w-full bg-input border border-border rounded-sm px-3 py-2 text-sm font-mono"
                data-testid="quicksend-to"
              />
            </Field>
            {draft.cc.length > 0 && (
              <Field label="Cc">
                <input
                  type="text"
                  value={draft.cc.join(", ")}
                  onChange={(e) => setDraft({ ...draft, cc: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  className="w-full bg-input border border-border rounded-sm px-3 py-2 text-sm font-mono"
                  data-testid="quicksend-cc"
                />
              </Field>
            )}
            <Field label="Subject">
              <input
                type="text"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                className="w-full bg-input border border-border rounded-sm px-3 py-2 text-sm"
                data-testid="quicksend-subject"
              />
            </Field>
            <Field label="Body">
              <textarea
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                rows={12}
                className="w-full bg-input border border-border rounded-sm px-3 py-2 text-sm font-sans leading-relaxed"
                data-testid="quicksend-body"
              />
            </Field>
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchDraft(true)}
                disabled={regen}
                data-testid="quicksend-regen"
              >
                {regen ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Regenerate
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={send}
                  disabled={sending}
                  className="bg-signal-500 hover:bg-signal-500/90 text-white"
                  data-testid="quicksend-send"
                >
                  {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Send via M365
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
